import {
  hasNoLoginMethod,
  type OAuthProviderName
} from '@shared/all/domains/authMethods';
import {
  SEEN_ACTIVATION_FUNNEL_KEY,
  shouldRequireOnboarding
} from '@shared/all/domains/onboarding';
import { useSyncExternalStore } from 'react';

// Device-level flag: once the funnel is skipped/completed on this device it isn't
// enforced again, even for a different account. Mirrors the mobile flag (same
// key, named once in shared so the "Join factiii" entry reads the same one).
const SEEN_KEY = SEEN_ACTIVATION_FUNNEL_KEY;

// Subscribers in THIS tab. The `storage` event fires only in other tabs, so a
// write here has to tell them itself or the overlay stays up until a reload.
const listeners = new Set<() => void>();

export const markFunnelSeen = () => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(SEEN_KEY, '1');
  listeners.forEach((notify) => notify());
};

// Forget that this device saw the funnel, so "Join factiii" opens it again.
// Only the /dev/onboarding harness calls this: nothing in the product un-sees
// the funnel.
export const clearFunnelSeen = () => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(SEEN_KEY);
  listeners.forEach((notify) => notify());
};

export const hasFunnelBeenSeen = (): boolean =>
  typeof window !== 'undefined' && localStorage.getItem(SEEN_KEY) === '1';

const subscribeFunnelSeen = (notify: () => void) => {
  listeners.add(notify);
  window.addEventListener('storage', notify);
  return () => {
    listeners.delete(notify);
    window.removeEventListener('storage', notify);
  };
};

// The device flag as a live value: it re-renders its reader when the flag is
// written in this tab or another. The server snapshot is "not seen", the only
// answer a server can give, so the first client render agrees with it.
export const useFunnelSeen = (): boolean =>
  useSyncExternalStore(subscribeFunnelSeen, hasFunnelBeenSeen, () => false);

// Single source of truth for "the onboarding overlay is active" so Main (which
// hides floating widgets) and OnboardingGate (which renders it) can't disagree.
// Account setup (magic-link accounts with no sign-in method) always shows; the
// funnel is suppressed once seen.
//
// A hook, not a plain call, because the seen flag lives in localStorage, which
// the server cannot read. Reading it during render made the server say
// "overlay" and a device that had seen the funnel say "no overlay", so the
// floating widgets hydrated one slot out and React threw the tree away: the
// server's Toaster landed where the client puts ChatWorkspace. The store's
// server snapshot is the answer the server already gave, so both first renders
// agree, and the device's own answer arrives in the re-render after hydration.
export const useShouldShowOnboardingOverlay = (user: {
  isAuthenticated: boolean | null;
  onboardingPath: string | null;
  hasPassword: boolean;
  hasPasskey: boolean;
  oauthProviders: OAuthProviderName[];
}): boolean => {
  const funnelSeen = useFunnelSeen();
  return (
    shouldRequireOnboarding(user) && (hasNoLoginMethod(user) || !funnelSeen)
  );
};
